import { supabase } from '../lib/supabase';
import {
  addLocalSuggestionVote,
  fetchVoteStatsFromPhp,
  getLocalVoteStats,
  submitVoteToPhp,
} from './suggestionVoteStore';

const PG_UNDEFINED_COLUMN = '42703';

function isMissingSoftwareColumn(error: { code?: string; message?: string } | null): boolean {
  if (!error) return false;
  if (error.code === PG_UNDEFINED_COLUMN) return true;
  const msg = (error.message || '').toLowerCase();
  return msg.includes('software') && (msg.includes('does not exist') || msg.includes('schema cache'));
}

function isMissingRelation(error: { code?: string; message?: string } | null): boolean {
  if (!error) return false;
  if (error.code === PG_UNDEFINED_COLUMN) return true;
  const msg = (error.message || '').toLowerCase();
  return msg.includes('does not exist') || msg.includes('relationship') || msg.includes('schema cache');
}

function isMissingSuggestionVotesTable(error: { code?: string; message?: string } | null): boolean {
  if (!error) return false;
  const msg = (error.message || '').toLowerCase();
  return (
    error.code === '42P01' ||
    error.code === 'PGRST205' ||
    msg.includes('suggestion_votes') ||
    msg.includes('schema cache') ||
    (msg.includes('does not exist') && msg.includes('relation'))
  );
}

let votesUseFallback: boolean | null = null;

function mergeVoteStats(
  base: Map<string, { supportCount: number; supportedByMe: boolean }>,
  extra: Map<string, { supportCount: number; supportedByMe: boolean }>
): Map<string, { supportCount: number; supportedByMe: boolean }> {
  for (const [id, row] of extra) {
    const current = base.get(id) ?? { supportCount: 0, supportedByMe: false };
    base.set(id, {
      supportCount: Math.max(current.supportCount, row.supportCount),
      supportedByMe: current.supportedByMe || row.supportedByMe,
    });
  }
  return base;
}

export type ForumSuggestionRow = {
  id: string;
  name: string;
  url: string | null;
  software?: string | null;
  category_id: string;
  category?: string | null;
  description: string | null;
  status: string;
  admin_notes: string | null;
  created_at: string;
  category_ref?: { name: string } | null;
};

/** Admin listesi — `software` sütunu yoksa eski kayıtlar yine yüklenir */
export async function fetchForumSuggestions(): Promise<ForumSuggestionRow[]> {
  const baseSelect = `
    id,
    name,
    url,
    category_id,
    category,
    description,
    status,
    admin_notes,
    created_at,
    category_ref:categories(name)
  `;

  let result = await supabase
    .from('forum_suggestions')
    .select(`${baseSelect}, software`)
    .order('created_at', { ascending: false });

  if (isMissingSoftwareColumn(result.error)) {
    result = await supabase
      .from('forum_suggestions')
      .select(baseSelect)
      .order('created_at', { ascending: false });
  }

  if (result.error) throw result.error;
  return (result.data || []) as ForumSuggestionRow[];
}

/** Öneri gönder — `software` sütunu yoksa yazılım olmadan kaydeder */
export async function insertForumSuggestion(
  row: Record<string, unknown>
): Promise<{ error: Error | null }> {
  const withSoftware = { ...row };
  let { error } = await supabase.from('forum_suggestions').insert([withSoftware]);

  if (error && 'software' in withSoftware && isMissingSoftwareColumn(error)) {
    const { software: _removed, ...withoutSoftware } = withSoftware;
    ({ error } = await supabase.from('forum_suggestions').insert([withoutSoftware]));
  }

  return { error: error ? new Error(error.message) : null };
}

export type PublicForumSuggestion = {
  id: string;
  name: string;
  url: string | null;
  software: string | null;
  categoryName: string;
  created_at: string;
  supportCount: number;
  supportedByMe: boolean;
};

type PendingRow = {
  id: string;
  name: string;
  url: string | null;
  software?: string | null;
  created_at: string;
  category?: string | null;
  category_ref?: { name: string } | null;
};

async function loadVoteStats(
  suggestionIds: string[],
  visitorHash: string
): Promise<Map<string, { supportCount: number; supportedByMe: boolean }>> {
  const stats = new Map<string, { supportCount: number; supportedByMe: boolean }>();
  if (!suggestionIds.length) return stats;

  for (const id of suggestionIds) {
    stats.set(id, { supportCount: 0, supportedByMe: false });
  }

  if (votesUseFallback !== true) {
    const { data, error } = await supabase
      .from('suggestion_votes')
      .select('suggestion_id, vote, voter_hash')
      .in('suggestion_id', suggestionIds);

    if (!error) {
      votesUseFallback = false;
      for (const vote of data ?? []) {
        const current = stats.get(vote.suggestion_id) ?? { supportCount: 0, supportedByMe: false };
        if (vote.vote === 1) {
          current.supportCount += 1;
          if (vote.voter_hash === visitorHash) current.supportedByMe = true;
        }
        stats.set(vote.suggestion_id, current);
      }
      return stats;
    }

    if (isMissingSuggestionVotesTable(error)) {
      votesUseFallback = true;
    } else {
      console.warn('suggestion_votes read:', error);
      return stats;
    }
  }

  const phpStats = await fetchVoteStatsFromPhp(suggestionIds, visitorHash);
  if (phpStats) {
    mergeVoteStats(stats, phpStats);
  }

  mergeVoteStats(stats, getLocalVoteStats(suggestionIds, visitorHash));
  return stats;
}

async function fetchPendingSuggestionRows(): Promise<{
  rows: PendingRow[];
  error: Error | null;
}> {
  const withCategory = `
    id,
    name,
    url,
    created_at,
    category,
    category_ref:categories(name)
  `;
  const plain = 'id, name, url, created_at, category';

  let result = await supabase
    .from('forum_suggestions')
    .select(`${withCategory}, software`)
    .eq('status', 'pending')
    .order('created_at', { ascending: false })
    .limit(50);

  if (isMissingSoftwareColumn(result.error)) {
    result = await supabase
      .from('forum_suggestions')
      .select(withCategory)
      .eq('status', 'pending')
      .order('created_at', { ascending: false })
      .limit(50);
  }

  if (result.error && isMissingRelation(result.error)) {
    result = await supabase
      .from('forum_suggestions')
      .select(`${plain}, software`)
      .eq('status', 'pending')
      .order('created_at', { ascending: false })
      .limit(50);
  }

  if (result.error && isMissingSoftwareColumn(result.error)) {
    result = await supabase
      .from('forum_suggestions')
      .select(plain)
      .eq('status', 'pending')
      .order('created_at', { ascending: false })
      .limit(50);
  }

  if (result.error) {
    return { rows: [], error: new Error(result.error.message) };
  }

  return { rows: (result.data || []) as PendingRow[], error: null };
}

/** Bekleyen öneriler + destek sayıları (Forum topluluğu sayfası) */
export async function fetchPublicPendingSuggestions(
  visitorHash: string
): Promise<{ data: PublicForumSuggestion[]; error: Error | null }> {
  const { rows, error } = await fetchPendingSuggestionRows();
  if (error) {
    return { data: [], error };
  }

  const voteStats = await loadVoteStats(
    rows.map((r) => r.id),
    visitorHash
  );

  const mapped = rows.map((row) => {
    const votes = voteStats.get(row.id) ?? { supportCount: 0, supportedByMe: false };
    return {
      id: row.id,
      name: row.name,
      url: row.url,
      software: row.software ?? null,
      categoryName: row.category_ref?.name ?? row.category?.trim() ?? '—',
      created_at: row.created_at,
      supportCount: votes.supportCount,
      supportedByMe: votes.supportedByMe,
    };
  });

  mapped.sort((a, b) => b.supportCount - a.supportCount || b.created_at.localeCompare(a.created_at));

  return { data: mapped, error: null };
}

async function submitSuggestionSupportFallback(
  suggestionId: string,
  visitorHash: string
): Promise<{ ok: boolean; error?: string }> {
  const php = await submitVoteToPhp(suggestionId, visitorHash);
  if (php.ok) return { ok: true };

  if (addLocalSuggestionVote(suggestionId, visitorHash)) {
    return { ok: true };
  }

  return { ok: false, error: php.error || 'Destek kaydedilemedi' };
}

/** Öneriyi destekle (oy = 1) */
export async function submitSuggestionSupport(
  suggestionId: string,
  visitorHash: string
): Promise<{ ok: boolean; error?: string }> {
  if (votesUseFallback === true) {
    return submitSuggestionSupportFallback(suggestionId, visitorHash);
  }

  const row = { suggestion_id: suggestionId, voter_hash: visitorHash, vote: 1 };
  const { error } = await supabase.from('suggestion_votes').upsert(row, {
    onConflict: 'suggestion_id,voter_hash',
  });

  if (!error) {
    votesUseFallback = false;
    return { ok: true };
  }

  if (error.code === '23505') {
    return { ok: true };
  }

  if (isMissingSuggestionVotesTable(error)) {
    votesUseFallback = true;
    return submitSuggestionSupportFallback(suggestionId, visitorHash);
  }

  if (error.code === '42501') {
    votesUseFallback = true;
    const fallback = await submitSuggestionSupportFallback(suggestionId, visitorHash);
    if (fallback.ok) return fallback;
    return { ok: false, error: 'Oylama izni yok. Supabase RLS politikalarını kontrol edin.' };
  }

  return { ok: false, error: error.message || 'Destek kaydedilemedi' };
}
